import { memo, useEffect, useState } from 'react';

const DEFAULT_COOLDOWN_SECONDS = 30;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const normalizeCooldown = (seconds) => {
  const value = Number(seconds);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : DEFAULT_COOLDOWN_SECONDS;
};

const ResendOtpTimer = memo(({ initialSeconds = DEFAULT_COOLDOWN_SECONDS, onResend }) => {
  const cooldownSeconds = normalizeCooldown(initialSeconds);
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    setSecondsLeft(cooldownSeconds);
  }, [cooldownSeconds]);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  const handleResend = async () => {
    if (secondsLeft > 0 || isResending) return;

    setIsResending(true);
    try {
      const nextCooldown = await onResend?.();
      setSecondsLeft(normalizeCooldown(nextCooldown ?? cooldownSeconds));
    } catch {
      // Keep the resend button available when the API fails.
    } finally {
      setIsResending(false);
    }
  };

  if (secondsLeft > 0) {
    return (
      <p className="w-full text-center desktop:text-[16px] text-sm leading-3 desktop:leading-4 text-merchant-text-secondary">
        Resend OTP in {formatTime(secondsLeft)}
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={handleResend}
      disabled={isResending}
      className="w-full cursor-pointer text-center desktop:text-[16px] text-sm leading-3 desktop:leading-4 text-merchant-action hover:underline disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isResending ? 'Sending OTP...' : 'Resend OTP'}
    </button>
  );
});

ResendOtpTimer.displayName = 'ResendOtpTimer';

export default ResendOtpTimer;
